/**
 * Sistema de logging seguro
 * - Em produção: remove todos os logs ou apenas logs de erro críticos
 * - Em desenvolvimento: permite logs detalhados mas remove dados sensíveis
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// Dados sensíveis que nunca devem ser logados
const SENSITIVE_KEYS = [
  'token',
  'password',
  'secret',
  'api_key',
  'apiKey',
  'auth',
  'authorization',
  'access_token',
  'refresh_token',
  'session',
  'cookie',
  'credit_card',
  'cvv',
  'ssn',
  'cpf',
  'cnpj',
];

// Campos que podem ser logados mas devem ser mascarados
const MASKABLE_KEYS = [
  'email',
  'phone',
  'telephone',
  'celular',
  'whatsapp',
];

/**
 * Remove ou mascara dados sensíveis de um objeto
 */
function sanitizeData(data: any, depth: number = 0): any {
  // Limitar profundidade para evitar loops infinitos
  if (depth > 5) {
    return '[Max depth reached]';
  }

  if (data === null || data === undefined) {
    return data;
  }

  // Se for string, verificar se contém dados sensíveis
  if (typeof data === 'string') {
    // Não logar strings muito longas (podem conter dados sensíveis)
    if (data.length > 500) {
      return `[String too long: ${data.length} chars]`;
    }
    return data;
  }

  // Se for número ou booleano, retornar como está
  if (typeof data !== 'object') {
    return data;
  }

  // Se for array, sanitizar cada item
  if (Array.isArray(data)) {
    return data.map((item, index) => {
      // Limitar arrays grandes
      if (index > 10) {
        return `[Array item ${index + 1}...]`;
      }
      return sanitizeData(item, depth + 1);
    });
  }

  // Se for objeto, sanitizar cada propriedade
  const sanitized: any = {};
  for (const key in data) {
    if (!data.hasOwnProperty(key)) continue;

    const lowerKey = key.toLowerCase();

    // Remover completamente dados sensíveis
    if (SENSITIVE_KEYS.some(sensitive => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
      continue;
    }

    // Mascarar dados que podem ser logados mas devem ser parcialmente ocultos
    if (MASKABLE_KEYS.some(maskable => lowerKey.includes(maskable))) {
      const value = String(data[key]);
      if (value.length > 4) {
        sanitized[key] = value.substring(0, 2) + '***' + value.substring(value.length - 2);
      } else {
        sanitized[key] = '***';
      }
      continue;
    }

    // Sanitizar recursivamente
    sanitized[key] = sanitizeData(data[key], depth + 1);
  }

  return sanitized;
}

/**
 * Logger seguro - só loga em desenvolvimento e remove dados sensíveis
 */
export const logger = {
  /**
   * Log de debug - apenas em desenvolvimento
   */
  debug: (...args: any[]) => {
    if (!isDevelopment) return;
    const sanitized = args.map(arg => sanitizeData(arg));
    console.log('[DEBUG]', ...sanitized);
  },

  /**
   * Log de informação - apenas em desenvolvimento
   */
  info: (...args: any[]) => {
    if (!isDevelopment) return;
    const sanitized = args.map(arg => sanitizeData(arg));
    console.log('[INFO]', ...sanitized);
  },

  /**
   * Log de aviso - apenas em desenvolvimento
   */
  warn: (...args: any[]) => {
    if (!isDevelopment) return;
    const sanitized = args.map(arg => sanitizeData(arg));
    console.warn('[WARN]', ...sanitized);
  },

  /**
   * Log de erro - sempre loga mas remove dados sensíveis
   */
  error: (...args: any[]) => {
    const sanitized = args.map(arg => sanitizeData(arg));
    console.error('[ERROR]', ...sanitized);
  },

  /**
   * Log de resposta da API - remove dados sensíveis
   */
  apiResponse: (url: string, response: any) => {
    if (!isDevelopment) return;
    const sanitizedResponse = sanitizeData(response);
    console.log('[API Response]', url, sanitizedResponse);
  },

  /**
   * Log de requisição da API - remove dados sensíveis
   */
  apiRequest: (method: string, url: string, data?: any) => {
    if (!isDevelopment) return;
    const sanitizedData = data ? sanitizeData(data) : undefined;
    console.log('[API Request]', method, url, sanitizedData || '');
  },

  /**
   * Log de WebSocket - remove dados sensíveis
   */
  websocket: (event: string, data?: any) => {
    if (!isDevelopment) return;
    const sanitizedData = data ? sanitizeData(data) : undefined;
    console.log('[WebSocket]', event, sanitizedData || '');
  },
};

/**
 * Função para remover todos os console.log em produção
 * (usar apenas se necessário remover completamente)
 */
export const disableConsoleInProduction = () => {
  if (isProduction) {
    // @ts-ignore
    console.log = () => {};
    // @ts-ignore
    console.debug = () => {};
    // @ts-ignore
    console.info = () => {};
    // @ts-ignore
    console.warn = () => {};
    // Manter apenas console.error para erros críticos
  }
};
